import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import {
  loadStoredTokens,
  clearStoredTokens,
  loginWithCredentials,
  refreshToken,
  type StoredTokens,
} from './authClient';

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  clearSession: () => void;
  getAccessTokenSilently: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function AuthProvider({ children }: { children: React.ReactNode }) {
  // Initialize from localStorage synchronously so tokens are available
  // before any child effects run (React runs child effects before parent effects)
  const initial = loadStoredTokens();
  const tokensRef = useRef<StoredTokens>(initial);

  const [isAuthenticated, setIsAuthenticated] = useState(
    () => !!initial.accessToken && initial.expiresAt > Date.now()
  );
  const [isLoading, setIsLoading] = useState(
    () =>
      !!initial.refreshToken &&
      !!initial.accessToken &&
      initial.expiresAt <= Date.now()
  );

  const setTokens = useCallback((tokens: StoredTokens) => {
    tokensRef.current = tokens;
    setIsAuthenticated(true);
  }, []);

  const clearSession = useCallback(() => {
    tokensRef.current = { accessToken: null, refreshToken: null, expiresAt: 0 };
    clearStoredTokens();
    setIsAuthenticated(false);
  }, []);

  const refreshAccessToken = useCallback(async (): Promise<string> => {
    const { refreshToken: currentRefreshToken } = tokensRef.current;
    if (!currentRefreshToken) {
      clearSession();
      throw new Error('No refresh token available');
    }

    try {
      const tokens = await refreshToken(currentRefreshToken);
      setTokens(tokens);
      return tokens.accessToken!;
    } catch {
      clearSession();
      throw new Error('Token refresh failed');
    }
  }, [clearSession, setTokens]);

  const getAccessTokenSilently = useCallback(async (): Promise<string> => {
    const { accessToken, expiresAt } = tokensRef.current;
    if (accessToken && expiresAt - Date.now() > 60_000) {
      return accessToken;
    }
    return refreshAccessToken();
  }, [refreshAccessToken]);

  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      const tokens = await loginWithCredentials(email, password);
      setTokens(tokens);
    },
    [setTokens]
  );

  const logout = useCallback(() => {
    clearSession();
  }, [clearSession]);

  // Try to refresh expired tokens on mount
  useEffect(() => {
    if (isLoading) {
      refreshAccessToken()
        .catch(() => {})
        .finally(() => setIsLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading,
        login,
        logout,
        clearSession,
        getAccessTokenSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthProvider;
